import React, { useState } from 'react';

import { Button, Flex, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import { useRunSchedulerMutation } from '@/store/slices/api/appointmentApiSlice';
import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { appointmentCreateBody } from '@/utils/appointmentFrequency';
import formatDate from '@/utils/formatDate';
import { runSchedulerForDevices } from '@/utils/runPatientScheduler';
import DatePicker from '@/components/DatePickers/DatePicker';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';
import { useAppointmentFrequency } from '../useAppointmentFrequency';

const InClinicSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  onSuccess,
  setLoading,
  patientSFId,
  deviceInfo,
  runSchedulerAfterCreate = true
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);

  const [createAppointment] = useCreateAppointmentMutation();
  const [runScheduler] = useRunSchedulerMutation();
  const { frequencyFor } = useAppointmentFrequency(patientSFId);

  const handleCreate = async () => {
    if (!scheduledDate || !patientSFId || !deviceInfo) return;

    setLoading(true);

    try {
      const formattedDate = formatDate(scheduledDate, 'YYYY-MM-DD');

      const appointmentData = appointmentCreateBody({
        deviceId: deviceInfo.deviceId,
        patientId: patientSFId,
        scheduledDate: formattedDate,
        type: 'in clinic',
        frequency: frequencyFor('in clinic', deviceInfo.type),
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      });

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        notifications.show({
          title: 'Error',
          message: result.message || 'Failed to create In Clinic appointment',
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      const failed = runSchedulerAfterCreate
        ? await runSchedulerForDevices(runScheduler, [deviceInfo.deviceId])
        : 0;
      notifications.show({
        title: 'Success',
        message: result.message || 'In Clinic appointment created successfully',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });
      if (failed > 0) {
        notifications.show({
          title: 'Schedule not adjusted',
          message: 'The in-clinic appointment was saved. The other dates could not be shifted.',
          color: 'yellow',
          autoClose: 5000
        });
      }

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating In Clinic appointment:', error);

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success) {
          notifications.show({
            title: 'Error',
            message: errorData.message || 'Failed to create In Clinic appointment',
            color: 'red',
            icon: <IconX size="1.1rem" />,
            autoClose: 5000
          });
          return;
        }
      }

      notifications.show({
        title: 'Error',
        message: 'Failed to create In Clinic appointment',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setScheduledDate(null);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (showSummary && apiResponse?.summary?.nextDates) {
    return (
      <SchedulesSummary
        appointmentType="In Clinic"
        nextDates={apiResponse.summary.nextDates}
        onClose={handleClose}
      />
    );
  }

  return (
    <Stack gap={'xs'}>
      <Flex px={20} pt={16} direction={'column'}>
        <Title order={5} fw={600} mb={4} c={'plum.9'}>
          In Clinic Appointment
        </Title>
        <Text size="xs" c={'plum'} mb={8}>
          Select a schedule date, including a past date. If any existing remote appointments
          conflict with account or patient level buffers, the dates will shift to comply.
        </Text>
        <Grid columns={6} mt={8}>
          <Grid.Col span={3}>
            <Text size="xs" c={'plum'} mb={4} fw={500}>
              Scheduled Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: scheduledDate,
                onChange: setScheduledDate,
                isClearable: false,
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
        </Grid>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleCreate}
          className={styles.modalButton}
          disabled={!scheduledDate || loading}
          loading={loading}>
          Create
        </Button>
      </Group>
    </Stack>
  );
};

export default InClinicSchedule;
